import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Dropdown, ProgressBar, Spinner, Modal } from 'react-bootstrap';
import { 
  FaSearch, 
  FaCalendarAlt, 
  FaEye, 
  FaEdit, 
  FaMapMarkerAlt,
  FaUserAlt,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaBell,
  FaDownload,
  FaEllipsisV,
  FaPhone,
  FaEnvelope,
  FaFileExcel,
  FaFilePdf,
  FaPaperPlane,
  FaTimes,
  FaSave
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../../../assets/css/admin/dashboard/UpcomingEvents.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';

const UpcomingEvents = () => {
  // State Management
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(4); // Changed to 4 for better pagination demo
  const [exportLoading, setExportLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  
  // Modal States
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Reminder Panel State
  const [showReminderPanel, setShowReminderPanel] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    scope: 'all',
    email: true,
    sms: true
  });

  // Sample Upcoming Events Data
  const [events] = useState([
    {
      id: 1,
      name: 'Sarah & John Wedding',
      type: 'Wedding Ceremony',
      date: '2026-02-15',
      time: '4:00 PM',
      venue: 'Grand Palace Hall, Lahore',
      venueShort: 'Grand Palace Hall',
      customer: 'Sarah Ahmed',
      customerPhone: '+92 300 1234567',
      customerEmail: 'sarah.ahmed@example.com',
      paymentStatus: 'Paid',
      paymentAmount: 350000,
      paidAmount: 350000,
      guests: 300,
      daysLeft: 15,
      icon: '💒',
      color: 'pink',
      description: 'A beautiful wedding ceremony for Sarah and John at the Grand Palace Hall.',
      specialRequirements: 'Photography team, Live band, Catering for 300 guests'
    },
    {
      id: 2,
      name: 'Ahmed & Fatima Engagement',
      type: 'Engagement Party',
      date: '2026-02-20',
      time: '6:00 PM',
      venue: 'Royal Garden Resort, Islamabad',
      venueShort: 'Royal Garden Resort',
      customer: 'Ahmed Hassan',
      customerPhone: '+92 301 9876543',
      customerEmail: 'ahmed.hassan@example.com',
      paymentStatus: 'Partial',
      paymentAmount: 150000,
      paidAmount: 75000,
      guests: 150,
      daysLeft: 20,
      icon: '💍',
      color: 'purple',
      description: 'Engagement celebration for Ahmed and Fatima with close family and friends.',
      specialRequirements: 'Outdoor decoration, DJ service, Buffet dinner'
    },
    {
      id: 3,
      name: 'Ali & Ariba Mehndi',
      type: 'Mehndi Ceremony',
      date: '2026-02-25',
      time: '7:00 PM',
      venue: 'Pearl Banquet, Karachi',
      venueShort: 'Pearl Banquet',
      customer: 'Ali Khan',
      customerPhone: '+92 333 4567890',
      customerEmail: 'ali.khan@example.com',
      paymentStatus: 'Paid',
      paymentAmount: 200000,
      paidAmount: 200000,
      guests: 200,
      daysLeft: 25,
      icon: '🎨',
      color: 'orange',
      description: 'Traditional Mehndi ceremony with cultural performances.',
      specialRequirements: 'Mehndi artists, Traditional decoration, Dhol players'
    },
    {
      id: 4,
      name: 'Hassan & Izna Nikah',
      type: 'Nikah Ceremony',
      date: '2026-03-05',
      time: '11:00 AM',
      venue: 'Green Valley Lawn, Multan',
      venueShort: 'Green Valley Lawn',
      customer: 'Hassan Raza',
      customerPhone: '+92 321 2345678',
      customerEmail: 'hassan.raza@example.com',
      paymentStatus: 'Pending',
      paymentAmount: 250000,
      paidAmount: 0,
      guests: 250,
      daysLeft: 33,
      icon: '🕌',
      color: 'green',
      description: 'Islamic wedding ceremony in a beautiful garden setting.',
      specialRequirements: 'Islamic scholar, Sound system, Seating arrangements'
    },
    {
      id: 5,
      name: 'Zara Birthday Party',
      type: 'Birthday Party',
      date: '2026-03-10',
      time: '5:00 PM',
      venue: 'Fun World Arena, Gujranwala',
      venueShort: 'Fun World Arena',
      customer: 'Zainab Hussain',
      customerPhone: '+92 345 8765432',
      customerEmail: 'zainab.hussain@example.com',
      paymentStatus: 'Partial',
      paymentAmount: 80000,
      paidAmount: 40000,
      guests: 80,
      daysLeft: 38,
      icon: '🎂',
      color: 'blue',
      description: 'Fun-filled birthday party for little Zara with games and activities.',
      specialRequirements: 'Bouncy castle, Clown entertainment, Birthday cake'
    }
  ]);

  // Stats Data
  const stats = [
    { label: 'Upcoming Events', value: '45', color: 'blue', icon: '📅' },
    { label: 'This Week', value: '8', color: 'purple', icon: '📍' },
    { label: 'Total Revenue', value: '₨ 2.5M', color: 'green', icon: '💰' },
    { label: 'Pending Payments', value: '₨ 350K', color: 'orange', icon: '⚠️' }
  ];

  // Filter events
  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.venue.toLowerCase().includes(searchTerm.toLowerCase());
    
    const eventDate = new Date(event.date);
    const today = new Date();
    const weekLater = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const monthLater = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    const matchesDate = dateFilter === 'all' || 
                       (dateFilter === 'week' && eventDate <= weekLater) ||
                       (dateFilter === 'month' && eventDate <= monthLater) ||
                       (dateFilter === 'urgent' && event.daysLeft <= 7);
    
    return matchesSearch && matchesDate;
  });

  // Pagination
  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  // Export Calendar Functions
  const handleExportCalendar = async (format, timeRange) => {
    setExportLoading(true);
    
    setTimeout(() => {
      setExportLoading(false);
      
      let message = '';
      if (format === 'ics') {
        message = 'Calendar exported successfully as Google Calendar (.ics)';
      } else if (format === 'pdf') {
        message = 'Schedule exported successfully as PDF';
      } else if (format === 'excel') {
        message = 'Schedule exported successfully as Excel';
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        confirmButtonText: 'OK',
        confirmButtonColor: '#7fd3c1',
        timer: 2000,
        timerProgressBar: true
      });
      
      console.log(`Exporting ${format} for ${timeRange}`);
    }, 1500);
  };

  // Send Reminders Functions
  const handleToggleReminderPanel = () => {
    setShowReminderPanel(!showReminderPanel);
  };

  const handleReminderSettingChange = (setting, value) => {
    setReminderSettings({
      ...reminderSettings,
      [setting]: value
    });
  };

  const handleSendReminders = async () => {
    if (!reminderSettings.email && !reminderSettings.sms) {
      Swal.fire({
        icon: 'warning',
        title: 'Select Reminder Type',
        text: 'Please select at least one reminder type (Email or SMS)',
        confirmButtonColor: '#7fd3c1'
      });
      return;
    }

    setReminderLoading(true);
    
    setTimeout(() => {
      setReminderLoading(false);
      setShowReminderPanel(false);
      
      const recipientCount = reminderSettings.scope === 'all' ? 45 : 8;
      const reminderTypes = [];
      if (reminderSettings.email) reminderTypes.push('Email');
      if (reminderSettings.sms) reminderTypes.push('SMS');
      
      Swal.fire({
        icon: 'success',
        title: 'Reminders Sent!',
        text: `Reminders sent successfully to ${recipientCount} users via ${reminderTypes.join(' and ')}`,
        confirmButtonText: 'OK',
        confirmButtonColor: '#7fd3c1',
        timer: 3000,
        timerProgressBar: true
      });
      
      console.log('Sending reminders:', reminderSettings);
    }, 2000);
  };

  // Handle Actions - Updated with Modal Support
  const handleView = (eventId) => {
    const event = events.find(e => e.id === eventId);
    setSelectedEvent(event);
    setShowViewModal(true);
  };

  const handleEdit = (eventId) => {
    const event = events.find(e => e.id === eventId);
    setSelectedEvent(event);
    setShowEditModal(true);
  };

  const handleNotify = (eventId) => {
    const event = events.find(e => e.id === eventId);
    
    Swal.fire({
      title: 'Send Reminder',
      text: `Send reminder to ${event.customer} for ${event.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#7fd3c1',
      cancelButtonColor: '#dc3545',
      confirmButtonText: 'Yes, Send!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'success',
          title: 'Reminder Sent!',
          text: `Reminder sent successfully to ${event.customer}`,
          confirmButtonColor: '#7fd3c1',
          timer: 2000,
          timerProgressBar: true
        });
      }
    });
  };

  const handleContact = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleEmail = (email) => {
    window.open(`mailto:${email}`, '_self');
  };

  const handleSaveEdit = () => {
    setShowEditModal(false);
    Swal.fire({
      icon: 'success',
      title: 'Event Updated!',
      text: 'Event details have been updated successfully',
      confirmButtonColor: '#7fd3c1',
      timer: 2000,
      timerProgressBar: true
    });
  };

  // Get Payment Badge
  const getPaymentBadge = (status) => {
    const statusConfig = {
      'Paid': { bg: 'success', icon: <FaCheckCircle />, text: 'Fully Paid' },
      'Partial': { bg: 'warning', icon: <FaClock />, text: 'Partial Payment' },
      'Pending': { bg: 'danger', icon: <FaExclamationTriangle />, text: 'Payment Pending' }
    };
    return statusConfig[status] || statusConfig['Pending'];
  };

  // Format Currency
  const formatCurrency = (amount) => {
    return `₨ ${(amount / 1000).toFixed(0)}K`;
  };

  return (
    <AdminLayout>
    <div className="ue-upcoming-events">
      <Container fluid>
        {/* Page Header */}
        <div className="ue-page-header">
          <div className="ue-header-content">
            <div className="ue-title-wrapper">
              <h2 className="ue-page-title">Upcoming Events</h2>
              <Badge className="ue-title-badge">
                <FaClock className="ue-badge-icon" />
                45 Events Scheduled
              </Badge>
            </div>
            <p className="ue-page-subtitle">Track and manage all upcoming wedding events</p>
          </div>
          <div className="ue-header-actions">
            {/* Export Calendar Dropdown */}
            <Dropdown className="ue-export-dropdown-wrapper">
              <Dropdown.Toggle className="ue-action-btn ue-btn-secondary" disabled={exportLoading}>
                {exportLoading ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <FaDownload className="ue-btn-icon" />
                )}
                Export Calendar
              </Dropdown.Toggle>

              <Dropdown.Menu className="ue-export-dropdown">
                <div className="ue-dropdown-section">
                  <div className="ue-dropdown-title">Export Calendar As</div>
                  <Dropdown.Item onClick={() => handleExportCalendar('ics', 'all')}>
                    <FaCalendarAlt className="ue-dropdown-icon ue-icon-calendar" />
                    Google Calendar (.ics)
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExportCalendar('pdf', 'all')}>
                    <FaFilePdf className="ue-dropdown-icon ue-icon-pdf" />
                    PDF Schedule
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExportCalendar('excel', 'all')}>
                    <FaFileExcel className="ue-dropdown-icon ue-icon-excel" />
                    Excel (.xlsx)
                  </Dropdown.Item>
                </div>
                <Dropdown.Divider />
                <div className="ue-dropdown-section">
                  <Dropdown.Item onClick={() => handleExportCalendar('ics', '7days')}>
                    <FaCalendarAlt className="ue-dropdown-icon ue-icon-week" />
                    Next 7 Days
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExportCalendar('ics', '30days')}>
                    <FaCalendarAlt className="ue-dropdown-icon ue-icon-month" />
                    Next 30 Days
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExportCalendar('ics', 'all')}>
                    <FaCalendarAlt className="ue-dropdown-icon ue-icon-all" />
                    All Upcoming
                  </Dropdown.Item>
                </div>
              </Dropdown.Menu>
            </Dropdown>

            {/* Send Reminders Button with Panel */}
            <div className="ue-reminder-wrapper">
              <Button 
                className="ue-action-btn ue-btn-primary" 
                onClick={handleToggleReminderPanel}
              >
                <FaBell className="ue-btn-icon" />
                Send Reminders
              </Button>

              {/* Reminder Panel */}
              {showReminderPanel && (
                <div className="ue-reminder-panel">
                  <div className="ue-reminder-header">
                    <h5 className="ue-reminder-title">Send Reminder To</h5>
                  </div>

                  <div className="ue-reminder-section">
                    <div className="ue-reminder-option">
                      <Form.Check
                        type="radio"
                        id="reminder-all"
                        label="All Upcoming Events"
                        name="reminderScope"
                        checked={reminderSettings.scope === 'all'}
                        onChange={() => handleReminderSettingChange('scope', 'all')}
                        className="ue-reminder-radio"
                      />
                    </div>
                    <div className="ue-reminder-option">
                      <Form.Check
                        type="radio"
                        id="reminder-week"
                        label="This Week Only"
                        name="reminderScope"
                        checked={reminderSettings.scope === 'week'}
                        onChange={() => handleReminderSettingChange('scope', 'week')}
                        className="ue-reminder-radio"
                      />
                    </div>
                  </div>

                  <div className="ue-reminder-divider"></div>

                  <div className="ue-reminder-section">
                    <h6 className="ue-reminder-subtitle">Reminder Type</h6>
                    <div className="ue-reminder-option">
                      <Form.Check
                        type="checkbox"
                        id="reminder-email"
                        label="Email"
                        checked={reminderSettings.email}
                        onChange={(e) => handleReminderSettingChange('email', e.target.checked)}
                        className="ue-reminder-checkbox"
                      />
                    </div>
                    <div className="ue-reminder-option">
                      <Form.Check
                        type="checkbox"
                        id="reminder-sms"
                        label="SMS"
                        checked={reminderSettings.sms}
                        onChange={(e) => handleReminderSettingChange('sms', e.target.checked)}
                        className="ue-reminder-checkbox"
                      />
                    </div>
                  </div>

                  <Button 
                    className="ue-reminder-send-btn"
                    onClick={handleSendReminders}
                    disabled={reminderLoading}
                  >
                    {reminderLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="me-2" />
                        Send Now
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="ue-stats-row">
          {stats.map((stat, index) => (
            <Col key={index} xl={3} lg={6} md={6} sm={12} className="mb-4">
              <Card className={`ue-stat-card ue-stat-${stat.color}`}>
                <Card.Body className="ue-stat-body">
                  <div className="ue-stat-header">
                    <div className="ue-stat-icon">{stat.icon}</div>
                  </div>
                  <h3 className="ue-stat-value">{stat.value}</h3>
                  <p className="ue-stat-label">{stat.label}</p>
                  <div className="ue-stat-progress">
                    <ProgressBar 
                      now={75} 
                      variant={stat.color === 'blue' ? 'info' : stat.color === 'purple' ? 'primary' : stat.color} 
                      className="ue-progress-bar"
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Main Content Card */}
        <Card className="ue-content-card">
          <Card.Body className="ue-card-body">
            {/* Filters Section */}
            <div className="ue-filters-section">
              <Row>
                <Col lg={6} md={12} sm={12}>
                  <div className="ue-filter-group">
                    <label className="ue-filter-label">
                      <FaSearch className="ue-label-icon" />
                      Search Events
                    </label>
                    <div className="ue-search-wrapper">
                      <FaSearch className="ue-input-icon" />
                      <Form.Control
                        type="text"
                        placeholder="Search by event, customer or venue..."
                        className="ue-search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </Col>

                <Col lg={4} md={6} sm={12}>
                  <div className="ue-filter-group">
                    <label className="ue-filter-label">
                      <FaCalendarAlt className="ue-label-icon" />
                      Filter by Date
                    </label>
                    <div className="ue-select-wrapper">
                      <FaCalendarAlt className="ue-input-icon" />
                      <Form.Select
                        className="ue-filter-select"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                      >
                        <option value="all">All Upcoming</option>
                        <option value="urgent">Urgent (Next 7 Days)</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                      </Form.Select>
                    </div>
                  </div>
                </Col>

                <Col lg={2} md={6} sm={12}>
                  <div className="ue-filter-group">
                    <label className="ue-filter-label" style={{ visibility: 'hidden' }}>Results</label>
                    <div className="ue-results-card">
                      <span className="ue-result-number">{currentEvents.length}</span>
                      <span className="ue-result-label">Events</span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Events Grid */}
            <Row className="ue-events-grid">
              {currentEvents.length > 0 ? (
                currentEvents.map((event) => (
                  <Col key={event.id} xl={6} lg={12} md={12} sm={12} className="mb-4">
                    <Card className="ue-event-card">
                      <Card.Body className="ue-event-body">
                        {/* Event Header */}
                        <div className="ue-event-header">
                          <div className="ue-event-icon-wrapper">
                            <div className="ue-event-icon">{event.icon}</div>
                            <div className="ue-urgency-badge">
                              {event.daysLeft <= 7 ? (
                                <Badge bg="danger" className="ue-urgent-badge">
                                  <FaClock /> URGENT
                                </Badge>
                              ) : (
                                <span className="ue-days-left">{event.daysLeft} days left</span>
                              )}
                            </div>
                          </div>
                          <Dropdown className="ue-card-dropdown" align="end">
                            <Dropdown.Toggle className="ue-dropdown-toggle" variant="link">
                              <FaEllipsisV />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="ue-dropdown-menu">
                              <Dropdown.Item onClick={() => handleView(event.id)}>
                                <FaEye className="ue-dropdown-icon" /> View Details
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleEdit(event.id)}>
                                <FaEdit className="ue-dropdown-icon" /> Edit Event
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleNotify(event.id)}>
                                <FaBell className="ue-dropdown-icon" /> Send Reminder
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>

                        {/* Event Info */}
                        <div className="ue-event-info">
                          <h3 className="ue-event-name">{event.name}</h3>
                          <span className="ue-event-type">{event.type}</span>
                        </div>

                        {/* Event Details */}
                        <div className="ue-event-details">
                          <div className="ue-detail-row">
                            <div className="ue-detail-icon-wrapper ue-icon-date">
                              <FaCalendarAlt />
                            </div>
                            <div className="ue-detail-content">
                              <span className="ue-detail-label">Date & Time</span>
                              <span className="ue-detail-value">
                                {new Date(event.date).toLocaleDateString('en-GB', { 
                                  day: 'numeric', 
                                  month: 'short', 
                                  year: 'numeric' 
                                })} • {event.time}
                              </span>
                            </div>
                          </div>

                          <div className="ue-detail-row">
                            <div className="ue-detail-icon-wrapper ue-icon-venue">
                              <FaMapMarkerAlt />
                            </div>
                            <div className="ue-detail-content">
                              <span className="ue-detail-label">Venue</span>
                              <span className="ue-detail-value">{event.venue}</span>
                            </div>
                          </div>

                          <div className="ue-detail-row">
                            <div className="ue-detail-icon-wrapper ue-icon-customer">
                              <FaUserAlt />
                            </div>
                            <div className="ue-detail-content">
                              <span className="ue-detail-label">Customer</span>
                              <span className="ue-detail-value">{event.customer}</span>
                              <div className="ue-contact-actions">
                                <button 
                                  className="ue-contact-btn"
                                  onClick={() => handleContact(event.customerPhone)}
                                  title="Call Customer"
                                >
                                  <FaPhone />
                                </button>
                                <button 
                                  className="ue-contact-btn"
                                  onClick={() => handleEmail(event.customerEmail)}
                                  title="Email Customer"
                                >
                                  <FaEnvelope />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Payment Section */}
                        <div className="ue-payment-section">
                          <div className="ue-payment-header">
                            <div className="ue-payment-title">
                              <FaMoneyBillWave className="ue-payment-icon" />
                              <span>Payment Status</span>
                            </div>
                            <Badge 
                              bg={getPaymentBadge(event.paymentStatus).bg} 
                              className="ue-payment-badge"
                            >
                              {getPaymentBadge(event.paymentStatus).icon} 
                              {getPaymentBadge(event.paymentStatus).text}
                            </Badge>
                          </div>

                          <div className="ue-payment-progress">
                            <div className="ue-payment-amounts">
                              <span className="ue-paid-amount">
                                {formatCurrency(event.paidAmount)} Paid
                              </span>
                              <span className="ue-total-amount">
                                {formatCurrency(event.paymentAmount)} Total
                              </span>
                            </div>
                            <ProgressBar 
                              now={(event.paidAmount / event.paymentAmount) * 100} 
                              variant={
                                event.paymentStatus === 'Paid' ? 'success' :
                                event.paymentStatus === 'Partial' ? 'warning' : 'danger'
                              }
                              className="ue-payment-bar"
                            />
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col xs={12}>
                  <div className="ue-no-events">
                    <div className="ue-no-events-content">
                      <span className="ue-no-events-icon">📅</span>
                      <h3 className="ue-no-events-title">No Upcoming Events Found</h3>
                      <p className="ue-no-events-text">Try adjusting your filters or search term</p>
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {/* Pagination */}
            {filteredEvents.length > eventsPerPage && (
              <div className="ue-pagination">
                <Button 
                  className="ue-page-btn ue-prev-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                
                <div className="ue-page-numbers">
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index}
                      className={`ue-page-number ${currentPage === index + 1 ? 'ue-active' : ''}`}
                      onClick={() => setCurrentPage(index + 1)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                
                <Button 
                  className="ue-page-btn ue-next-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* View Details Modal */}
      <Modal 
        show={showViewModal} 
        onHide={() => setShowViewModal(false)}
        size="lg"
        centered
        className="ue-detail-modal"
      >
        <Modal.Header className="ue-modal-header">
          <Modal.Title className="ue-modal-title">
            <FaEye className="ue-modal-icon" />
            Event Details
          </Modal.Title>
          <button className="ue-modal-close" onClick={() => setShowViewModal(false)}>
            <FaTimes />
          </button>
        </Modal.Header>
        <Modal.Body className="ue-modal-body">
          {selectedEvent && (
            <div className="ue-detail-content">
              {/* Event Icon & Name */}
              <div className="ue-modal-event-header">
                <div className="ue-modal-event-icon">{selectedEvent.icon}</div>
                <div>
                  <h3 className="ue-modal-event-name">{selectedEvent.name}</h3>
                  <Badge bg="info" className="ue-modal-event-type">{selectedEvent.type}</Badge>
                </div>
              </div>

              {/* Event Information Grid */}
              <Row className="ue-modal-info-grid">
                <Col md={6}>
                  <div className="ue-info-item">
                    <FaCalendarAlt className="ue-info-icon ue-icon-blue" />
                    <div>
                      <span className="ue-info-label">Event Date & Time</span>
                      <span className="ue-info-value">
                        {new Date(selectedEvent.date).toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })} • {selectedEvent.time}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="ue-info-item">
                    <FaClock className="ue-info-icon ue-icon-orange" />
                    <div>
                      <span className="ue-info-label">Days Remaining</span>
                      <span className="ue-info-value">{selectedEvent.daysLeft} days left</span>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="ue-info-item">
                    <FaMapMarkerAlt className="ue-info-icon ue-icon-red" />
                    <div>
                      <span className="ue-info-label">Venue</span>
                      <span className="ue-info-value">{selectedEvent.venue}</span>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="ue-info-item">
                    <FaUserAlt className="ue-info-icon ue-icon-purple" />
                    <div>
                      <span className="ue-info-label">Expected Guests</span>
                      <span className="ue-info-value">{selectedEvent.guests} Guests</span>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Customer Details */}
              <div className="ue-modal-section">
                <h5 className="ue-section-title">Customer Information</h5>
                <div className="ue-customer-details">
                  <div className="ue-customer-avatar-large">
                    {selectedEvent.customer.charAt(0)}
                  </div>
                  <div className="ue-customer-info-details">
                    <h6>{selectedEvent.customer}</h6>
                    <p><FaPhone className="me-2" />{selectedEvent.customerPhone}</p>
                    <p><FaEnvelope className="me-2" />{selectedEvent.customerEmail}</p>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="ue-modal-section">
                <h5 className="ue-section-title">Payment Information</h5>
                <div className="ue-payment-details">
                  <div className="ue-payment-row">
                    <span>Total Amount:</span>
                    <strong>₨ {selectedEvent.paymentAmount.toLocaleString()}</strong>
                  </div>
                  <div className="ue-payment-row">
                    <span>Paid Amount:</span>
                    <strong className="text-success">₨ {selectedEvent.paidAmount.toLocaleString()}</strong>
                  </div>
                  <div className="ue-payment-row">
                    <span>Remaining:</span>
                    <strong className="text-danger">
                      ₨ {(selectedEvent.paymentAmount - selectedEvent.paidAmount).toLocaleString()}
                    </strong>
                  </div>
                  <div className="ue-payment-row">
                    <span>Status:</span>
                    <Badge bg={getPaymentBadge(selectedEvent.paymentStatus).bg}>
                      {selectedEvent.paymentStatus}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="ue-modal-section">
                <h5 className="ue-section-title">Event Description</h5>
                <p className="ue-description-text">{selectedEvent.description}</p>
              </div>

              {/* Special Requirements */}
              <div className="ue-modal-section">
                <h5 className="ue-section-title">Special Requirements</h5>
                <p className="ue-requirements-text">{selectedEvent.specialRequirements}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="ue-modal-footer">
          <Button className="ue-modal-btn ue-btn-secondary-modal" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button className="ue-modal-btn ue-btn-primary-modal" onClick={() => {
            setShowViewModal(false);
            handleEdit(selectedEvent.id);
          }}>
            <FaEdit className="me-2" />
            Edit Event
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Event Modal */}
      <Modal 
        show={showEditModal} 
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
        className="ue-detail-modal"
      >
        <Modal.Header className="ue-modal-header ue-modal-header-edit">
          <Modal.Title className="ue-modal-title">
            <FaEdit className="ue-modal-icon" />
            Edit Event
          </Modal.Title>
          <button className="ue-modal-close" onClick={() => setShowEditModal(false)}>
            <FaTimes />
          </button>
        </Modal.Header>
        <Modal.Body className="ue-modal-body">
          {selectedEvent && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="ue-form-label">Event Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      defaultValue={selectedEvent.name}
                      className="ue-form-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="ue-form-label">Event Type</Form.Label>
                    <Form.Select className="ue-form-control" defaultValue={selectedEvent.type}>
                      <option>Wedding Ceremony</option>
                      <option>Engagement Party</option>
                      <option>Mehndi Ceremony</option>
                      <option>Nikah Ceremony</option>
                      <option>Birthday Party</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="ue-form-label">Event Date</Form.Label>
                    <Form.Control 
                      type="date" 
                      defaultValue={selectedEvent.date}
                      className="ue-form-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="ue-form-label">Event Time</Form.Label>
                    <Form.Control 
                      type="time" 
                      defaultValue={selectedEvent.time}
                      className="ue-form-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="ue-form-label">Venue</Form.Label>
                    <Form.Control 
                      type="text" 
                      defaultValue={selectedEvent.venue}
                      className="ue-form-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="ue-form-label">Customer Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      defaultValue={selectedEvent.customer}
                      className="ue-form-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="ue-form-label">Guest Count</Form.Label>
                    <Form.Control 
                      type="number" 
                      defaultValue={selectedEvent.guests}
                      className="ue-form-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="ue-form-label">Description</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3}
                      defaultValue={selectedEvent.description}
                      className="ue-form-control"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="ue-modal-footer">
          <Button className="ue-modal-btn ue-btn-secondary-modal" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button className="ue-modal-btn ue-btn-success-modal" onClick={handleSaveEdit}>
            <FaSave className="me-2" />
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </AdminLayout>
  );
};

export default UpcomingEvents;